/** The replay page's state and its two reads.
 *
 * Extracted from the page so the component stays a rendering concern: the
 * awkward parts here are the defaults (which index and session to open on)
 * and the resets (what has to be forgotten when you switch index), and
 * both are easier to follow away from the JSX.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { simulatorApi, type SimChain, type SimSessions } from "@/lib/simulatorApi";
import { SESSION_OPEN, step, stepSession } from "@/lib/simulatorClock";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

export interface SimulatorState {
  indices: UseQueryResult<SimSessions[]>;
  chain: UseQueryResult<SimChain>;
  underlying: string | null;
  tradeDate: string | null;
  /** Captured sessions for the current index, ascending — what the day
   * buttons step along. */
  sessions: string[];
  at: string;
  expiry: string | null;
  pickIndex: (next: string) => void;
  pickDate: (next: string) => void;
  setAt: (next: string) => void;
  setExpiry: (next: string) => void;
  stepBy: (minutes: number, floor?: string) => void;
  stepDay: (days: number) => void;
}

interface Selection {
  underlying: string | null;
  tradeDate: string | null;
  at: string;
  expiry: string | null;
}

/** How long the date must hold still before the page asks for it. Clicking through dates used to fire a request (and, behind it, a
 * 10-200 s Athena load) for every date passed; the minute stays immediate, because stepping minutes is a cache hit. */
const DATE_SETTLE_MS = 350;

function useChainQuery(sel: Selection): UseQueryResult<SimChain> {
  const asked = useMemo(
    () => ({ underlying: sel.underlying, tradeDate: sel.tradeDate, expiry: sel.expiry }),
    [sel.underlying, sel.tradeDate, sel.expiry],
  );
  const settled = useDebouncedValue(asked, DATE_SETTLE_MS);
  return useQuery({
    queryKey: ["simulator-chain", settled.underlying, settled.tradeDate, sel.at, settled.expiry],
    // The signal makes a request for a date you have clicked past CANCEL, freeing the browser's connection instead of sitting
    // "pending" in front of every other call the page makes.
    queryFn: ({ signal }) =>
      simulatorApi.chain(
        {
          underlying: settled.underlying ?? "",
          tradeDate: settled.tradeDate ?? "",
          at: sel.at,
          ...(settled.expiry ? { expiry: settled.expiry } : {}),
        },
        signal,
      ),
    enabled: settled.underlying !== null && settled.tradeDate !== null,
    // Keeps the previous chain on screen while the next lands, so stepping
    // does not flash empty between minutes.
    placeholderData: keepPreviousData,
  });
}

/** What the page opens on. The API orders indices by coverage, so the
 * first is the best-covered one and its LAST session is the most recent —
 * the two defaults a reader almost always wants. */
function useOpeningDefaults(
  data: SimSessions[] | undefined,
  underlying: string | null,
  setUnderlying: (v: string) => void,
  setTradeDate: (v: string) => void,
): void {
  useEffect(() => {
    const first = data?.[0];
    if (first && underlying === null) {
      setUnderlying(first.underlying);
      setTradeDate(first.last);
    }
  }, [data, underlying, setUnderlying, setTradeDate]);
}

/** The day buttons. Split out only to keep `useSimulatorState` inside the
 * line limit; the whole rule is that the clock stays put, because the
 * point of a day step is to see the SAME minute on another session. */
function useDayStep(
  sessions: string[],
  tradeDate: string | null,
  pickDate: (next: string) => void,
): (days: number) => void {
  return useCallback(
    (days: number): void => {
      if (tradeDate === null) return;
      const next = stepSession(sessions, tradeDate, days);
      if (next !== tradeDate) pickDate(next);
    },
    [sessions, tradeDate, pickDate],
  );
}

export function useSimulatorState(): SimulatorState {
  const [underlying, setUnderlying] = useState<string | null>(null);
  const [tradeDate, setTradeDate] = useState<string | null>(null);
  const [at, setAt] = useState(SESSION_OPEN);
  const [expiry, setExpiry] = useState<string | null>(null);

  const indices = useQuery({
    queryKey: ["simulator-indices"],
    queryFn: () => simulatorApi.indices(),
  });

  useOpeningDefaults(indices.data, underlying, setUnderlying, setTradeDate);

  const chain = useChainQuery({ underlying, tradeDate, at, expiry });

  const pickIndex = useCallback(
    (next: string): void => {
      setUnderlying(next);
      // ⭐ KEEP THE DAY IF THE NEW INDEX HAS IT. Switching index is usually
      // asking "what was SENSEX doing at this moment", and jumping to that
      // index's most recent session throws away the very thing being
      // compared. It only falls back to the latest when the new index was
      // not captured that day — which is the real constraint, since
      // indices joined the capture at different times and asking for a
      // date one of them lacks is a 404 the picker cannot explain.
      const row = indices.data?.find((i) => i.underlying === next);
      const keep = tradeDate !== null && (row?.sessions.includes(tradeDate) ?? false);
      setTradeDate(keep ? tradeDate : (row?.last ?? null));
      // Expiries are per index AND per session, so a held one is wrong
      // either way.
      setExpiry(null);
    },
    [indices.data, tradeDate],
  );

  const pickDate = useCallback((next: string): void => {
    setTradeDate(next);
    // Expiries are per session; a held one can be absent from the new day.
    setExpiry(null);
  }, []);

  const stepBy = useCallback((minutes: number, floor?: string): void => {
    setAt((current) => step(current, minutes, floor));
  }, []);

  const sessions = useMemo(
    () => indices.data?.find((i) => i.underlying === underlying)?.sessions ?? [],
    [indices.data, underlying],
  );
  const stepDay = useDayStep(sessions, tradeDate, pickDate);

  return {
    indices,
    chain,
    underlying,
    tradeDate,
    sessions,
    at,
    expiry,
    pickIndex,
    pickDate,
    setAt,
    setExpiry,
    stepBy,
    stepDay,
  };
}
